import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';

/** Paper card used under a simple page header (filters, chips, book details). */
export const simpleUnderHeaderClass =
  'rounded-[28px] border border-[#e4d5c3] bg-[#fffdf8] p-3 text-start shadow-sm dark:border-[#3d342c] dark:bg-[#1c1714] sm:p-4';

/**
 * Simple-theme page title. Same shape as the badge section bar:
 * a back button and the title in one tile.
 */
export const SimplePageHeader: React.FC<{
  title: React.ReactNode;
  /** Small label sitting just above the title, inside the same tile. */
  eyebrow?: React.ReactNode;
  /** Second line under the eyebrow, before the title (for example a tag name). */
  eyebrowDetail?: React.ReactNode;
  /** Keep the eyebrow as written. Lesson and book labels stay small capitals. */
  eyebrowPlain?: boolean;
  onBack?: () => void;
  backLabel?: string;
  tourId?: string;
  /** Sits above the title tile (rating, views, favorite). */
  above?: React.ReactNode;
}> = ({ title, eyebrow, eyebrowDetail, eyebrowPlain = false, onBack, backLabel, tourId, above }) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';

  return (
    <div className="space-y-2">
      {above ? <div className="px-1">{above}</div> : null}
      <div
        dir={direction}
        {...(tourId ? { 'data-tour-id': tourId } : {})}
        className="flex items-center gap-3 rounded-[28px] border border-[#e4d5c3] bg-[#f7f1e8] px-3 py-3 text-start dark:border-[#3d342c] dark:bg-[#1c1714] sm:px-5 sm:py-4"
      >
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex min-h-12 shrink-0 items-center gap-2 rounded-2xl bg-[#3f2a56] px-4 text-sm font-semibold text-[#fff8ee] hover:bg-[#4a3264] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c4a574]"
            aria-label={backLabel || t('mmeBack')}
          >
            <ChevronRight className={`h-4 w-4 ${isRtl ? '' : 'rotate-180'}`} aria-hidden />
            {t('mmeBack')}
          </button>
        ) : null}
        <div className="min-w-0">
          {eyebrow ? (
            <p className={`text-[10px] font-semibold text-[#8a6a45] dark:text-[#d4b48a] ${eyebrowPlain ? '' : 'uppercase tracking-[0.16em]'}`}>
              {eyebrow}
            </p>
          ) : null}
          {eyebrowDetail ? (
            <p className="text-[11px] font-semibold normal-case tracking-normal text-[#6b5344] dark:text-[#e7d3bc]">
              {eyebrowDetail}
            </p>
          ) : null}
          <h1 className="line-clamp-2 text-xl font-semibold text-[#2b221b] dark:text-[#f6efe6] sm:text-2xl">{title}</h1>
        </div>
      </div>
    </div>
  );
};
